// ═══════════════════════════════════════════════════════════════════════════
// FeedMapping — a connection's stores → locations, each location's time zone
// and trading day cutoff, and each platform code → an EXISTING channel.
// ═══════════════════════════════════════════════════════════════════════════
// Controlled editor. The parent passes the connection's stores (feedApi
// listStores) and the account's locations with their channels (feedApi
// getMappingTargets); onSave(payload) receives the exact saveMappings body
// for the items the admin changed, and nothing else:
//   { stores?:      [{ id, workspaceId }],
//     workspaces?:  [{ id, timeZone, tradingDayCutoff }],
//     channelMaps?: [{ workspaceId, platformCode, channelId, feedFrom }] }
// Every item names every field, and "" becomes null (unmap a store, midnight,
// remove a route, mapped but not live). onSave may reject with a FeedApiError;
// its code is shown translated.
//
// The server's rules (contract 5.8) are pre-empted with disabled controls and
// a reason, so the admin isn't told off after typing:
//  • a channel is never created: a picker lists that location's channels only;
//  • a channel fed by another connection is taken (channel_already_fed);
//  • a channel this connection maps under another code, or another row of
//    this form already uses, can't be picked (channel_has_other_code);
//  • zone and cutoff are read-only once a platform there has a feed start
//    (workspace_is_fed);
//  • a feed start needs the location's zone (time_zone_required), which the
//    same save may set: the server applies stores, then workspaces, then
//    channel maps.
// Removing a live route or moving its start later can still be refused once
// the feed has written days (the screen can't see fed days); a warning says so.
//
// A save that hands days up to today to the feed asks first (ActionDialog):
// the same PUT replaces those days' typed rows (contract 5.8; kept for
// restore), and once the feed writes rows the start can't move later, so a
// mistyped year would cost a year of typed P&L. "Today" is the browser's
// local date; the optional `today` prop (YYYY-MM-DD) pins it for tests.
// ═══════════════════════════════════════════════════════════════════════════
import { useId, useState } from "react";
import { useT } from "../lib/i18n";
import {
  platformInfo, suggestChannelId, normPlatformCode, isValidTimeZone, timeZoneOptions,
  suggestedTimeZone, hhmm, isValidCutoff, isDay, fmtDateTime, fmtDay,
} from "../lib/feedUi";
import {
  inputStyle, selectStyleAdmin, adminThStyle, adminTdStyle, codeStyle, btnPrimaryDark, btnSecondary,
  smallBtn, disabledLook, mutedText, labelStyle, hintStyle, fieldErrorStyle, emptyRowStyle,
  tableStyle, theadRowStyle, rowBorder, Banner, FeedErrorText,
} from "./feedKit";
import { ActionDialog } from "./FeedDialogs";

const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
const routeKey = (wsId, code) => `${wsId}\n${code}`;

// The browser's local calendar day, "YYYY-MM-DD" (as lib/dailyLogFeed.js ymd).
function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Does saving this route hand the feed days up to today? Those are the days
// whose typed rows the save replaces: from the new start on a channel the
// route didn't feed before (a new route, a start where there was none, or
// another channel), else from the new start up to the saved one. A start
// still ahead replaces nothing yet; moving a start later replaces nothing.
function replacesTypedDays(r, today) {
  const { channelId, feedFrom } = r.route;
  if (!channelId || !isDay(feedFrom) || feedFrom > today) return false;
  if (channelId !== r.saved.channelId || !r.saved.feedFrom) return true;
  return feedFrom < r.saved.feedFrom;
}

export function FeedMapping({ connectionId, stores = [], workspaces = [], onSave, today = null }) {
  const { t } = useT();
  const zoneListId = useId();
  const [storeDraft, setStoreDraft] = useState({});   // store id → workspace id | ""
  const [wsDraft, setWsDraft] = useState({});         // workspace id → { timeZone, tradingDayCutoff }
  const [routeDraft, setRouteDraft] = useState({});   // routeKey → { channelId, feedFrom }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);   // the "replace typed sales" dialog is open

  // ── effective values: the draft where the admin changed something, else saved
  const storeWs = (s) => (has(storeDraft, s.id) ? storeDraft[s.id] : (s.workspaceId || ""));
  const savedZone = (w) => w.timeZone || "";
  const savedCutoff = (w) => hhmm(w.tradingDayCutoff);
  const zoneOf = (w) => (has(wsDraft, w.id) ? wsDraft[w.id].timeZone : savedZone(w));
  const cutoffOf = (w) => (has(wsDraft, w.id) ? wsDraft[w.id].tradingDayCutoff : savedCutoff(w));
  const isFed = (w) => w.channels.some((ch) => ch.channelMap && ch.channelMap.feedFrom);
  const savedRoute = (w, code) => {
    const ch = w.channels.find((c) => c.channelMap && c.channelMap.connectionId === connectionId
      && normPlatformCode(c.channelMap.platformCode) === code);
    return ch ? { channelId: ch.id, feedFrom: ch.channelMap.feedFrom || "" } : { channelId: "", feedFrom: "" };
  };
  const routeOf = (w, code) => {
    const k = routeKey(w.id, code);
    return has(routeDraft, k) ? routeDraft[k] : savedRoute(w, code);
  };

  // ── which locations get a zone block and platform rows: those with a store
  //    mapped to them in this form, or with a route of this connection.
  const locations = [];
  for (const w of workspaces) {
    const codes = new Map();
    let mapped = 0;
    for (const s of stores) {
      if (storeWs(s) !== w.id) continue;
      mapped += 1;
      for (const p of s.platforms || []) {
        const c = normPlatformCode(p.platformCode);
        if (!c) continue;
        const cur = codes.get(c) || { seen: 0, held: 0 };
        codes.set(c, { seen: cur.seen + (p.ordersSeen || 0), held: cur.held + (p.ordersHeld || 0) });
      }
    }
    for (const ch of w.channels) {
      const m = ch.channelMap;
      const c = m && m.connectionId === connectionId ? normPlatformCode(m.platformCode) : "";
      if (c && !codes.has(c)) codes.set(c, { seen: 0, held: 0 });
    }
    if (mapped === 0 && codes.size === 0) continue;
    const rows = [...codes.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([code, n]) => ({ code, ...n, route: routeOf(w, code), saved: savedRoute(w, code) }));
    locations.push({ w, rows });
  }

  // ── dirty items and validation (visible locations only)
  const dirtyStores = stores.filter((s) => storeWs(s) !== (s.workspaceId || ""));
  const dirtyWs = [];
  const dirtyRoutes = [];
  const zoneProblem = {};    // workspace id → "required" | "invalid"
  const cutoffProblem = {};  // workspace id → true
  let blocked = false;
  for (const { w, rows } of locations) {
    const zone = zoneOf(w).trim();
    const cutoff = cutoffOf(w);
    const wsChanged = zone !== savedZone(w) || cutoff !== savedCutoff(w);
    let needsZone = false;
    for (const r of rows) {
      if (r.route.channelId !== r.saved.channelId || r.route.feedFrom !== r.saved.feedFrom) {
        dirtyRoutes.push({ w, r });
        if (r.route.feedFrom) needsZone = true;
        if (r.route.feedFrom && (!r.route.channelId || !isDay(r.route.feedFrom))) blocked = true;
      }
    }
    if ((wsChanged || needsZone) && !isValidTimeZone(zone)) {
      zoneProblem[w.id] = zone ? "invalid" : "required";
      blocked = true;
    }
    if (wsChanged && !isValidCutoff(cutoff)) {
      cutoffProblem[w.id] = true;
      blocked = true;
    }
    if (wsChanged) dirtyWs.push({ w, zone, cutoff });
  }
  const changeCount = dirtyStores.length + dirtyWs.length + dirtyRoutes.length;
  const canSave = changeCount > 0 && !blocked && !busy;
  const todayYmd = isDay(today) ? today : localToday();
  const backdated = dirtyRoutes.filter(({ r }) => replacesTypedDays(r, todayYmd));

  function buildPayload() {
    const payload = {};
    if (dirtyStores.length) {
      payload.stores = dirtyStores.map((s) => ({ id: s.id, workspaceId: storeWs(s) || null }));
    }
    if (dirtyWs.length) {
      payload.workspaces = dirtyWs.map(({ w, zone, cutoff }) => ({ id: w.id, timeZone: zone, tradingDayCutoff: cutoff || null }));
    }
    if (dirtyRoutes.length) {
      payload.channelMaps = dirtyRoutes.map(({ w, r }) => ({
        workspaceId: w.id,
        platformCode: r.code,
        channelId: r.route.channelId || null,
        feedFrom: r.route.feedFrom || null,
      }));
    }
    return payload;
  }

  function undo() {
    setStoreDraft({});
    setWsDraft({});
    setRouteDraft({});
    setError(null);
  }

  async function doSave() {
    await onSave(buildPayload());
    setStoreDraft({});
    setWsDraft({});
    setRouteDraft({});
  }

  async function save() {
    if (!canSave) return;
    setError(null);
    if (backdated.length > 0) {
      setConfirming(true);   // nothing is sent until the admin confirms
      return;
    }
    setBusy(true);
    try {
      await doSave();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  // The confirm dialog's action: a refusal is shown inside the dialog (it
  // stays open) and the changes stay.
  async function confirmSave() {
    setBusy(true);
    try {
      await doSave();
    } finally {
      setBusy(false);
    }
  }

  const setStore = (id, value) => setStoreDraft((d) => ({ ...d, [id]: value }));
  const setWs = (w, patch) => setWsDraft((d) => ({
    ...d,
    [w.id]: { timeZone: zoneOf(w), tradingDayCutoff: cutoffOf(w), ...(d[w.id] || {}), ...patch },
  }));
  const setRoute = (w, code, patch) => {
    const k = routeKey(w.id, code);
    setRouteDraft((d) => {
      const cur = has(d, k) ? d[k] : savedRoute(w, code);
      const next = { ...cur, ...patch };
      if (!next.channelId) next.feedFrom = "";   // no channel, no feed start
      return { ...d, [k]: next };
    });
  };

  if (stores.length === 0) {
    return <div style={emptyRowStyle}>{t("feed.map.storesEmpty")}</div>;
  }

  const activeWorkspaces = workspaces.filter((w) => !w.archivedAt);
  const brands = new Set(stores.map((s) => s.providerBrandId).filter(Boolean));
  const zones = timeZoneOptions();

  return (
    <div>
      {/* ── Stores ─────────────────────────────────────────────────── */}
      <div style={{ overflowX:"auto" }}>
        <table style={tableStyle}>
          <thead>
            <tr style={theadRowStyle}>
              <th style={adminThStyle}>{t("feed.map.colStore")}</th>
              <th style={adminThStyle}>{t("feed.map.colBrand")}</th>
              <th style={adminThStyle}>{t("feed.map.colPos")}</th>
              <th style={{ ...adminThStyle, textAlign:"end" }}>{t("feed.map.colHeld")}</th>
              <th style={adminThStyle}>{t("feed.map.colLastSeen")}</th>
              <th style={adminThStyle}>{t("feed.map.colLocation")}</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((s, i) => {
              const current = storeWs(s);
              const savedWs = s.workspaceId ? workspaces.find((w) => w.id === s.workspaceId) : null;
              const label = `${t("feed.map.colLocation")}: ${s.storeName || s.providerStoreId}${s.brandName ? ` · ${s.brandName}` : ""}`;
              return (
                <tr key={s.id} style={rowBorder(i)}>
                  <td style={adminTdStyle}>
                    <div style={{ fontWeight:600, color:"#0f172a" }}>{s.storeName || "—"}</div>
                    <div className="num" style={mutedText}>{s.providerStoreId}</div>
                  </td>
                  <td style={adminTdStyle}>
                    <div>{s.brandName || (s.providerBrandId ? "—" : t("feed.map.noBrand"))}</div>
                    {s.providerBrandId && <div className="num" style={mutedText}>{s.providerBrandId}</div>}
                  </td>
                  <td style={adminTdStyle}>{s.posStoreRef ? <code style={codeStyle}>{s.posStoreRef}</code> : "—"}</td>
                  <td className="num" style={{ ...adminTdStyle, textAlign:"end", fontWeight:600, color: s.ordersHeld > 0 && !current ? "#9a6b10" : "#0f172a" }}>
                    {s.ordersHeld}
                  </td>
                  <td className="num" style={{ ...adminTdStyle, color:"#64748b", fontSize:12 }}>{fmtDateTime(s.lastSeenAt)}</td>
                  <td style={adminTdStyle}>
                    <select aria-label={label} value={current} onChange={(e) => setStore(s.id, e.target.value)} disabled={busy}
                      style={{ ...selectStyleAdmin, minWidth:200, borderColor: current !== (s.workspaceId || "") ? "#93c5fd" : "#e2e8f0" }}>
                      <option value="">{t("feed.map.notMapped")}</option>
                      {activeWorkspaces.map((w) => (
                        <option key={w.id} value={w.id}>{w.name || w.id}{w.currency ? ` (${w.currency})` : ""}</option>
                      ))}
                      {savedWs && savedWs.archivedAt && (
                        <option value={savedWs.id} disabled>{(savedWs.name || savedWs.id) + t("feed.map.archived")}</option>
                      )}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {brands.size > 1 && (
        <Banner kind="info" style={{ marginTop:12 }}>{t("feed.map.multiBrand")}</Banner>
      )}

      {/* ── Locations: zone, cutoff and platforms ──────────────────── */}
      <div style={{ marginTop:20, fontSize:12.5, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:.6 }}>
        {t("feed.map.locationsTitle")}
      </div>
      <div style={{ ...hintStyle, marginBottom:10 }}>
        {t("feed.map.generalNote")} {t("feed.map.cofundNote")} {t("feed.map.channelsNote")}
      </div>
      {locations.length === 0 && <div style={{ ...emptyRowStyle, padding:20 }}>{t("feed.map.noLocations")}</div>}
      {zones.length > 0 && (
        <datalist id={zoneListId}>
          {zones.map((z) => <option key={z} value={z} />)}
        </datalist>
      )}
      {locations.map(({ w, rows }) => {
        const fed = isFed(w);
        const archived = Boolean(w.archivedAt);
        const locked = fed || archived;
        const zone = zoneOf(w);
        const cutoff = cutoffOf(w);
        const suggestion = suggestedTimeZone(w.currency);
        const zoneId = `${zoneListId}-${w.id}-zone`;
        const cutoffId = `${zoneListId}-${w.id}-cutoff`;
        return (
          <section key={w.id} aria-label={w.name || w.id}
            style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:14, marginBottom:14, background:"#fff" }}>
            <div style={{ display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap", marginBottom:10 }}>
              <div style={{ fontSize:14, fontWeight:700, color:"#0f172a" }}>{w.name || w.id}</div>
              {w.currency && <span style={mutedText}>{w.currency}</span>}
              {archived && <span style={mutedText}>{t("feed.map.archived")}</span>}
            </div>

            <div style={{ display:"flex", gap:18, flexWrap:"wrap", alignItems:"flex-start" }}>
              <div style={{ flex:"1 1 220px", minWidth:0 }}>
                <label htmlFor={zoneId} style={labelStyle}>{t("feed.map.timeZone")}</label>
                <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                  <input id={zoneId} value={zone} disabled={locked || busy} list={zones.length > 0 ? zoneListId : undefined}
                    onChange={(e) => setWs(w, { timeZone: e.target.value })}
                    placeholder={suggestion || ""} spellCheck={false} autoComplete="off"
                    style={{ ...inputStyle, width:"100%", maxWidth:260, background: locked ? "#f8fafc" : "#fff" }} />
                  {!locked && suggestion && zone.trim() !== suggestion && (
                    <button type="button" onClick={() => setWs(w, { timeZone: suggestion })} disabled={busy} style={smallBtn}>
                      {t("feed.map.useZone")}{suggestion}
                    </button>
                  )}
                </div>
                {zoneProblem[w.id] === "required" && <div style={fieldErrorStyle}>{t("feed.map.timeZoneRequired")}</div>}
                {zoneProblem[w.id] === "invalid" && <div style={fieldErrorStyle}>{t("feed.map.timeZoneInvalid")}</div>}
              </div>
              <div style={{ flex:"1 1 200px", minWidth:0 }}>
                <label htmlFor={cutoffId} style={labelStyle}>{t("feed.map.cutoff")}</label>
                <input id={cutoffId} type="time" value={cutoff} disabled={locked || busy}
                  onChange={(e) => setWs(w, { tradingDayCutoff: e.target.value })}
                  style={{ ...inputStyle, width:130, background: locked ? "#f8fafc" : "#fff" }} />
                {cutoffProblem[w.id]
                  ? <div style={fieldErrorStyle}>{t("feed.map.cutoffInvalid")}</div>
                  : <div style={hintStyle}>{t("feed.map.cutoffHint")}</div>}
              </div>
            </div>
            {fed && <div style={{ ...hintStyle, marginTop:8 }}>{t("feed.map.zoneLocked")}</div>}

            <div style={{ overflowX:"auto", marginTop:12 }}>
              {rows.length === 0 ? (
                <div style={{ ...emptyRowStyle, padding:16 }}>{t("feed.map.noPlatforms")}</div>
              ) : (
                <table style={tableStyle}>
                  <thead>
                    <tr style={theadRowStyle}>
                      <th style={adminThStyle}>{t("feed.map.colCode")}</th>
                      <th style={{ ...adminThStyle, textAlign:"end" }}>{t("feed.map.colSeen")}</th>
                      <th style={adminThStyle}>{t("feed.map.colChannel")}</th>
                      <th style={adminThStyle}>{t("feed.map.colFeedFrom")}</th>
                      <th style={adminThStyle}>{t("feed.map.colNotes")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <PlatformRow key={r.code} index={i} w={w} row={r} connectionId={connectionId}
                        otherRoutes={rows.filter((o) => o.code !== r.code)} busy={busy || archived}
                        onChange={(patch) => setRoute(w, r.code, patch)} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        );
      })}

      {/* ── Save bar ───────────────────────────────────────────────── */}
      {error && <Banner kind="error" style={{ marginBottom:10 }}><FeedErrorText error={error} /></Banner>}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:10, flexWrap:"wrap" }}>
        {changeCount > 0 && (
          <span style={{ fontSize:12, color:"#64748b" }}>
            {t("feed.map.changes")}<span className="num" style={{ fontWeight:700, color:"#0f172a" }}>{changeCount}</span>
          </span>
        )}
        {changeCount > 0 && (
          <button type="button" onClick={undo} disabled={busy} style={disabledLook(btnSecondary, busy)}>{t("feed.map.undo")}</button>
        )}
        <button type="button" onClick={save} disabled={!canSave} style={disabledLook(btnPrimaryDark, !canSave)}>
          {busy ? t("feed.saving") : t("feed.map.save")}
        </button>
      </div>

      {/* ── Confirm: these starts replace typed sales now ──────────── */}
      {confirming && (
        <ActionDialog title={t("feed.map.backdateTitle")} body={t("feed.map.backdateBody")}
          confirmLabel={t("feed.map.backdateConfirm")} run={confirmSave} onClose={() => setConfirming(false)}
          subject={backdated.map(({ w, r }, i) => {
            const ch = w.channels.find((c) => c.id === r.route.channelId);
            return (
              <span key={`${w.id}\n${r.code}`}>
                {i > 0 && <br />}
                <b>{w.name || w.id}</b>{" · "}{ch && ch.platform ? `${ch.platform} ` : ""}<code style={codeStyle}>{r.code}</code>
                {t("feed.map.backdateFrom")}<b className="num">{fmtDay(r.route.feedFrom)}</b>
              </span>
            );
          })} />
      )}
    </div>
  );
}

// One platform code at one location: its channel, feed start and notes.
function PlatformRow({ index, w, row, connectionId, otherRoutes, busy, onChange }) {
  const { t } = useT();
  const info = platformInfo(row.code);
  const suggestedId = suggestChannelId(row.code, w.channels);
  const { channelId, feedFrom } = row.route;
  const usedByOtherRow = new Set(otherRoutes.map((o) => o.route.channelId).filter(Boolean));
  const liveRemoved = Boolean(row.saved.feedFrom) && !channelId;
  const movedLater = Boolean(row.saved.feedFrom) && Boolean(channelId)
    && (!feedFrom || feedFrom > row.saved.feedFrom);
  const suggestedChannel = suggestedId ? w.channels.find((ch) => ch.id === suggestedId) : null;
  return (
    <tr style={rowBorder(index)}>
      <td style={adminTdStyle}><code style={codeStyle}>{row.code}</code></td>
      <td className="num" style={{ ...adminTdStyle, textAlign:"end" }}>{row.seen}</td>
      <td style={adminTdStyle}>
        <select aria-label={`${t("feed.map.colChannel")}: ${row.code}`} value={channelId} disabled={busy}
          onChange={(e) => onChange({ channelId: e.target.value })}
          style={{ ...selectStyleAdmin, minWidth:200, borderColor: channelId !== row.saved.channelId ? "#93c5fd" : "#e2e8f0" }}>
          <option value="">{t("feed.map.notTracked")}</option>
          {w.channels.map((ch) => {
            const m = ch.channelMap;
            const takenOther = Boolean(m) && m.connectionId !== connectionId;
            const otherCode = Boolean(m) && m.connectionId === connectionId && normPlatformCode(m.platformCode) !== row.code;
            const inForm = ch.id !== channelId && usedByOtherRow.has(ch.id);
            const tag = takenOther ? t("feed.map.takenOther") : (otherCode || inForm) ? t("feed.map.takenCode") : "";
            return (
              <option key={ch.id} value={ch.id} disabled={takenOther || otherCode || inForm}>
                {(ch.platform || ch.id) + (ch.isActive ? "" : t("feed.map.inactive")) + tag
                  + (ch.id === suggestedId ? t("feed.map.suggestedTag") : "")}
              </option>
            );
          })}
        </select>
        {!channelId && suggestedChannel && !info.own && (
          <div style={hintStyle}>{t("feed.map.suggested")}{suggestedChannel.platform}</div>
        )}
        {liveRemoved && <div style={{ ...hintStyle, color:"#9a6b10" }}>{t("feed.map.liveRemoveWarn")}</div>}
      </td>
      <td style={adminTdStyle}>
        <input type="date" aria-label={`${t("feed.map.colFeedFrom")}: ${row.code}`} value={feedFrom}
          disabled={busy || !channelId} onChange={(e) => onChange({ feedFrom: e.target.value })}
          style={{ ...inputStyle, width:150, borderColor: feedFrom !== row.saved.feedFrom ? "#93c5fd" : "#e2e8f0" }} />
        <div style={hintStyle}>{channelId ? t("feed.map.feedFromHint") : t("feed.map.feedFromNeedsChannel")}</div>
        {movedLater && <div style={{ ...hintStyle, color:"#9a6b10" }}>{t("feed.map.laterWarn")}</div>}
      </td>
      <td style={{ ...adminTdStyle, maxWidth:300 }}>
        {info.noteKeys.map((k) => (
          <div key={k} style={{ fontSize:12, color:"#475569", lineHeight:1.45, marginBottom:3 }}>{t(k)}</div>
        ))}
      </td>
    </tr>
  );
}
